import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../ui/dialog';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import { Badge } from '../../../../ui/badge';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';

interface Tag {
  id: string;
  name: string;
  color: string | null;
}

interface ManageTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: Tag[];
  onTagsChange: () => void;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export function ManageTagsDialog({ open, onOpenChange, tags, onTagsChange }: ManageTagsDialogProps) {
  const { merchant } = useMerchant();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLORS[0]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleCreate = async () => {
    if (!merchant || !newName.trim()) return;
    const { error } = await epicengageSupabase
      .from('tags')
      .insert({ merchant_id: merchant.id, name: newName.trim(), color: newColor });
    if (error) {
      if (error.code === '23505') toast.error('Tag already exists');
      else toast.error(error.message);
      return;
    }
    setNewName('');
    onTagsChange();
  };

  const handleDelete = async (id: string) => {
    const { error } = await epicengageSupabase.from('tags').delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    onTagsChange();
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) return;
    const { error } = await epicengageSupabase.from('tags').update({ name: editName.trim() }).eq('id', id);
    if (error) {
      if (error.code === '23505') toast.error('Tag name already exists');
      else toast.error(error.message);
      return;
    }
    setEditingId(null);
    onTagsChange();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Tags</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Create */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Tag name"
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    className={`w-6 h-6 rounded-full border-2 ${newColor === c ? 'border-foreground' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setNewColor(c)}
                  />
                ))}
              </div>
            </div>
            <Button size="sm" onClick={handleCreate}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* List */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {tags.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No tags yet</p>}
            {tags.map((tag) => (
              <div key={tag.id} className="flex items-center justify-between p-2 rounded-lg border">
                {editingId === tag.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-8"
                      onKeyDown={(e) => e.key === 'Enter' && handleUpdate(tag.id)}
                    />
                    <Button size="sm" variant="ghost" onClick={() => handleUpdate(tag.id)}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                ) : (
                  <>
                    <Badge style={tag.color ? { backgroundColor: tag.color, color: 'white' } : undefined}>
                      {tag.name}
                    </Badge>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(tag.id); setEditName(tag.name); }}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(tag.id)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
